import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js'
import {
  User,
  Briefcase,
  Bell,
  Lock,
  Save,
  AlertCircle,
  CheckCircle,
  Globe,
  Target,
  CreditCard,
  Receipt,
  DollarSign,
  Calendar,
  Clock,
  Upload,
} from 'lucide-react'
import { profileApi } from '../../lib/api/profile'
import { sessionsApi } from '../../lib/api/sessions'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useProfileCompletion } from '../../contexts/profileCompletion'
import { formatDateInTz } from '../../lib/utils/timezone'

interface ProfileData {
  name: string
  email: string
  phone: string
  timezone: string
  language: string
  linkedin_url: string
  portfolio_url: string
  experience_level: string
  target_role: string
  resume_url: string
  job_description: string
}

interface NotificationSettings {
  email_session_confirmation: boolean
  email_session_reminder: boolean
  email_feedback_ready: boolean
  sms_session_reminder: boolean
  [key: string]: boolean
}

interface PasswordData {
  current: string
  new_password: string
  confirm: string
}

interface PaymentRecord {
  id: number
  amount: number
  currency: string
  status: string
  created_at: string
  session_title?: string
  scheduled_at?: string
  interviewer_name?: string
  payment_intent_id?: string
}

type TabId = 'personal' | 'professional' | 'preferences' | 'payments' | 'account'

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'personal', label: 'Personal Info', icon: User },
  { id: 'professional', label: 'Professional Info', icon: Briefcase },
  { id: 'preferences', label: 'Preferences', icon: Bell },
  { id: 'payments', label: 'Payment History', icon: CreditCard },
  { id: 'account', label: 'Account', icon: Lock },
]

const TAB_ORDER: TabId[] = ['personal', 'professional', 'preferences', 'payments', 'account']

export default function ClientProfile() {
  const { refresh: refreshCompletion } = useProfileCompletion()
  const navigate = useNavigate()
  const { user, setUser } = useAuthStore()
  const [activeTab, setActiveTab] = useState<TabId>('personal')
  const [payments, setPayments] = useState<PaymentRecord[]>([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [profile, setProfile] = useState<ProfileData>({
    name: '',
    email: '',
    phone: '',
    timezone: '',
    language: '',
    linkedin_url: '',
    portfolio_url: '',
    experience_level: '',
    target_role: '',
    resume_url: '',
    job_description: '',
  })
  const [settings, setSettings] = useState<NotificationSettings>({
    email_session_confirmation: true,
    email_session_reminder: true,
    email_feedback_ready: true,
    sms_session_reminder: false,
  })
  const [prefsSaving, setPrefsSaving] = useState(false)
  const [passwords, setPasswords] = useState<PasswordData>({
    current: '',
    new_password: '',
    confirm: '',
  })
  const [phoneCountry, setPhoneCountry] = useState('US')
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await profileApi.getProfile()
        const savedPhone = data.phone || ''
        // Only auto-switch the country dropdown when the saved phone is in
        // E.164 form (starts with `+`). Otherwise keep the US default — we do
        // not infer a country from locale or unprefixed digits.
        let displayPhone = savedPhone
        if (savedPhone.startsWith('+')) {
          const parsed = parsePhoneNumberFromString(savedPhone)
          if (parsed?.country) {
            setPhoneCountry(parsed.country)
            displayPhone = parsed.nationalNumber
          }
        }
        setProfile({
          name: data.full_name || data.name || '',
          email: data.email || '',
          phone: displayPhone,
          timezone: data.timezone || '',
          language: data.language || '',
          linkedin_url: data.linkedin_url || '',
          portfolio_url: data.portfolio_url || '',
          experience_level: data.experience_level || '',
          target_role: data.target_role || '',
          resume_url: data.resume_url || '',
          job_description: data.job_description || data.interview_goals || '',
        })
      } catch (err) {
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [])

  useEffect(() => {
    if (activeTab === 'payments') {
      setPaymentsLoading(true)
      sessionsApi
        .getPaymentHistory()
        .then((data) => setPayments(data || []))
        .catch(() => {})
        .finally(() => setPaymentsLoading(false))
    }
    if (activeTab === 'preferences') {
      profileApi.getNotificationPrefs()
        .then((data) => {
          setSettings({
            email_session_confirmation: data.email_session_confirmation ?? true,
            email_session_reminder: data.email_session_reminder ?? true,
            email_feedback_ready: data.email_feedback_ready ?? true,
            sms_session_reminder: data.sms_session_reminder ?? false,
          })
        })
        .catch(() => {})
    }
  }, [activeTab])

  const handleProfileChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setProfile({ ...profile, [e.target.name]: e.target.value })
  }

  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings({ ...settings, [e.target.name]: e.target.checked })
  }

  const handleSavePreferences = async () => {
    setPrefsSaving(true)
    try {
      await Promise.all([
        profileApi.updateNotificationPrefs(settings),
        profileApi.updateClientProfile({
          timezone: profile.timezone,
          language: profile.language,
        }),
      ])
      toast.success('Preferences saved!')
      refreshCompletion()
      navigate('/client/schedule')
    } catch (err) {
      toast.error('Failed to save preferences')
    } finally {
      setPrefsSaving(false)
    }
  }

  const handleSave = async () => {
    if (!profile.name.trim()) {
      toast.error('Full name is required')
      return
    }
    if (activeTab === 'professional' && !profile.experience_level) {
      toast.error('Experience level is required')
      return
    }
    if (profile.phone) {
      const parsed = parsePhoneNumberFromString(profile.phone, phoneCountry as any)
      if (!parsed || !parsed.isValid()) {
        toast.error('Please enter a valid phone number for the selected country')
        return
      }
      // Normalize to E.164 before saving
      setProfile((prev) => ({ ...prev, phone: parsed.format('E.164') }))
      profile.phone = parsed.format('E.164')
    }
    setSaving(true)
    try {
      await profileApi.updateClientProfile({
        full_name: profile.name,
        phone: profile.phone,
        timezone: profile.timezone,
        language: profile.language,
        current_title: profile.experience_level,
        target_role: profile.target_role,
        experience_level: profile.experience_level,
        linkedin_url: profile.linkedin_url,
        interview_goals: profile.job_description,
      })
      if (resumeFile) {
        const result = await profileApi.uploadResume(resumeFile)
        if (result?.resume_url) {
          setProfile((prev) => ({ ...prev, resume_url: result.resume_url }))
        }
        setResumeFile(null)
        const input = document.getElementById('client-resume-input') as HTMLInputElement | null
        if (input) input.value = ''
      }
      toast.success('Profile updated successfully!')
      refreshCompletion()
      if (user && profile.name) setUser({ ...user, name: profile.name })
      // Auto-advance to next tab
      const currentIdx = TAB_ORDER.indexOf(activeTab)
      const nextTab = TAB_ORDER[currentIdx + 1]
      if (nextTab && nextTab !== 'payments' && nextTab !== 'account') {
        setActiveTab(nextTab)
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update profile info'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordChange = async () => {
    if (passwords.new_password !== passwords.confirm) {
      toast.error('New passwords do not match')
      return
    }
    if (!passwords.current || !passwords.new_password) {
      toast.error('Please fill in all password fields')
      return
    }
    setSaving(true)
    try {
      await profileApi.changePassword({
        current_password: passwords.current,
        new_password: passwords.new_password,
        new_password_confirmation: passwords.confirm,
      })
      toast.success('Password updated successfully!')
      setPasswords({ current: '', new_password: '', confirm: '' })
    } catch (err) {
      toast.error('Failed to update password. Check your current password.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading profile..." />
  }

  return (
    <div className="client-profile">
      <div className="page-header">
        <h1 className="page-title">My Profile</h1>
        <p className="page-subtitle">
          Manage your personal information, preferences, and account settings
        </p>
      </div>

      <div className="profile-tabs-wrap">
        <div className="profile-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              type="button"
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'personal' && (
        <div className="tab-content">
          <div className="card">
            <div className="card-hdr">
              <User size={20} />
              <h3>Personal Information</h3>
            </div>
            <div className="profile-form">
              <div className="form-group">
                <label className="form-label">Full Name <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={profile.name}
                  onChange={handleProfileChange}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email Address <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={profile.email}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number <span style={{ color: '#dc2626' }}>*</span></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select
                      value={phoneCountry}
                      onChange={(e) => {
                        setPhoneCountry(e.target.value)
                        setProfile((prev) => ({ ...prev, phone: '' }))
                      }}
                      className="form-input"
                      style={{ width: 130, flexShrink: 0 }}
                    >
                      <option value="US">🇺🇸 +1 US</option>
                      <option value="GB">🇬🇧 +44 UK</option>
                      <option value="IN">🇮🇳 +91 India</option>
                      <option value="AU">🇦🇺 +61 AU</option>
                      <option value="CA">🇨🇦 +1 CA</option>
                      <option value="DE">🇩🇪 +49 DE</option>
                      <option value="FR">🇫🇷 +33 FR</option>
                      <option value="AE">🇦🇪 +971 UAE</option>
                      <option value="SG">🇸🇬 +65 SG</option>
                      <option value="JP">🇯🇵 +81 JP</option>
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      className="form-input"
                      value={profile.phone}
                      onChange={(e) => {
                        const formatted = new AsYouType(phoneCountry as any).input(e.target.value)
                        setProfile((prev) => ({ ...prev, phone: formatted }))
                      }}
                      placeholder="9876543210"
                      style={{ flex: 1 }}
                    />
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    Will be saved as E.164 format (e.g. +12125551234)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="profile-actions">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'professional' && (
        <div className="tab-content">
          <div className="card">
            <div className="card-hdr">
              <Briefcase size={20} />
              <h3>Professional Details</h3>
            </div>
            <div className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Experience Level <span style={{ color: '#dc2626' }}>*</span></label>
                  <select
                    name="experience_level"
                    className="form-input"
                    value={profile.experience_level}
                    onChange={handleProfileChange}
                  >
                    <option value="">Select experience level...</option>
                    <option value="entry">Entry-Level / Early Career (0 – 2 years)</option>
                    <option value="mid">Individual Contributor (IC) – Mid Level (3 – 5+ years)</option>
                    <option value="leadership">Leadership (Manager / Director / Above)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <Target
                      size={16}
                      style={{ marginRight: 8, verticalAlign: 'middle' }}
                    />
                    Target Role(s) <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="target_role"
                    className="form-input"
                    value={profile.target_role}
                    onChange={handleProfileChange}
                    placeholder="e.g., Senior Software Engineer, Product Manager"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">LinkedIn Profile URL <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="url"
                  name="linkedin_url"
                  className="form-input"
                  value={profile.linkedin_url}
                  onChange={handleProfileChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Portfolio / Other Links <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="url"
                  name="portfolio_url"
                  className="form-input"
                  value={profile.portfolio_url}
                  onChange={handleProfileChange}
                  placeholder="https://yourportfolio.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Resume <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  id="client-resume-input"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={(e) => setResumeFile(e.target.files?.[0] ?? null)}
                />

                {/* Uploaded resume card — shown when a resume exists and no new file is pending */}
                {profile.resume_url && !resumeFile && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 18px',
                    background: '#f0fdf4',
                    border: '1.5px solid #86efac',
                    borderRadius: 10,
                    marginBottom: 12,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 8,
                      background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <CheckCircle size={20} color="#16a34a" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d' }}>Resume uploaded</div>
                      <div style={{ fontSize: 12, color: '#16a34a', marginTop: 2 }}>Your resume is saved and visible to interviewers</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <a
                        href={profile.resume_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 13, fontWeight: 500, color: 'var(--primary-navy)',
                          padding: '6px 14px', border: '1px solid var(--primary-navy)',
                          borderRadius: 7, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        View
                      </a>
                      <button
                        type="button"
                        onClick={() => document.getElementById('client-resume-input')?.click()}
                        style={{
                          fontSize: 13, fontWeight: 500, color: '#6b7280',
                          padding: '6px 14px', border: '1px solid #d1d5db',
                          borderRadius: 7, background: 'white', cursor: 'pointer',
                        }}
                      >
                        Replace
                      </button>
                    </div>
                  </div>
                )}

                {/* New file selected — pending save */}
                {resumeFile && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 18px',
                    background: '#eff6ff',
                    border: '1.5px solid #93c5fd',
                    borderRadius: 10,
                    marginBottom: 12,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 8,
                      background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Upload size={18} color="#2563eb" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1d4ed8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {resumeFile.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>Ready to upload — click Save Changes</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setResumeFile(null)
                        const input = document.getElementById('client-resume-input') as HTMLInputElement | null
                        if (input) input.value = ''
                      }}
                      style={{ fontSize: 13, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', flexShrink: 0 }}
                    >
                      Remove
                    </button>
                  </div>
                )}

                {/* Upload drop zone — shown when no resume uploaded and no file selected */}
                {!profile.resume_url && !resumeFile && (
                  <div
                    style={{
                      border: '2px dashed #d1d5db', borderRadius: 10, padding: 28,
                      textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s',
                    }}
                    onClick={() => document.getElementById('client-resume-input')?.click()}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary-navy)'; (e.currentTarget as HTMLDivElement).style.background = '#f8fafc' }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#d1d5db'; (e.currentTarget as HTMLDivElement).style.background = '' }}
                  >
                    <Upload size={28} color="#9ca3af" style={{ marginBottom: 10 }} />
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Click to upload your resume</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>PDF, DOC, or DOCX — max 5 MB</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="profile-actions">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'preferences' && (
        <div className="tab-content">
          {/* Language & Timezone */}
          <div className="card">
            <div className="card-hdr">
              <Globe size={20} />
              <h3>Language & Timezone</h3>
            </div>
            <div className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Preferred Language <span style={{ color: '#dc2626' }}>*</span></label>
                  <select
                    name="language"
                    className="form-input"
                    value={profile.language}
                    onChange={handleProfileChange}
                  >
                    <option value="">Select language</option>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="zh">Chinese (Mandarin)</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="pt">Portuguese</option>
                    <option value="hi">Hindi</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Timezone <span style={{ color: '#dc2626' }}>*</span></label>
                  <select
                    name="timezone"
                    className="form-input"
                    value={profile.timezone}
                    onChange={handleProfileChange}
                  >
                    <option value="">Select timezone</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="America/Anchorage">Alaska Time (AKT)</option>
                    <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                    <option value="Europe/London">Greenwich Mean Time (GMT)</option>
                    <option value="Europe/Paris">Central European Time (CET)</option>
                    <option value="Asia/Kolkata">India Standard Time (IST)</option>
                    <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-hdr">
              <Bell size={20} />
              <h3>Notification Preferences</h3>
            </div>
            <div className="profile-form">
              <label className="toggle-row">
                <span>Session confirmation emails</span>
                <input
                  type="checkbox"
                  name="email_session_confirmation"
                  checked={settings.email_session_confirmation}
                  onChange={handleSettingsChange}
                />
              </label>
              <label className="toggle-row">
                <span>Email reminders for upcoming sessions</span>
                <input
                  type="checkbox"
                  name="email_session_reminder"
                  checked={settings.email_session_reminder}
                  onChange={handleSettingsChange}
                />
              </label>
              <label className="toggle-row">
                <span>Feedback delivery notifications</span>
                <input
                  type="checkbox"
                  name="email_feedback_ready"
                  checked={settings.email_feedback_ready}
                  onChange={handleSettingsChange}
                />
              </label>
              <label className="toggle-row">
                <span>SMS reminders</span>
                <input
                  type="checkbox"
                  name="sms_session_reminder"
                  checked={settings.sms_session_reminder}
                  onChange={handleSettingsChange}
                />
              </label>
            </div>
          </div>

          <div className="profile-actions">
            <button
              className="btn btn-primary"
              onClick={handleSavePreferences}
              disabled={prefsSaving}
            >
              <Save size={18} />
              {prefsSaving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="tab-content">
          <div className="card">
            <div className="card-hdr">
              <Receipt size={20} />
              <h3>Payment History</h3>
            </div>
            {paymentsLoading ? (
              <LoadingSpinner message="Loading payments..." />
            ) : payments.length === 0 ? (
              <div className="empty-payments">
                <CreditCard size={40} />
                <h4>No payments yet</h4>
                <p>
                  Your payment history will appear here after you book your
                  first session.
                </p>
              </div>
            ) : (
              <>
                <div className="payments-summary-row">
                  <div className="payment-stat">
                    <DollarSign size={18} />
                    <div>
                      <span className="stat-value">
                        $
                        {payments
                          .filter((p) => p.status === 'succeeded')
                          .reduce((sum, p) => sum + p.amount, 0)
                          .toFixed(2)}
                      </span>
                      <span className="stat-label-text">Total Spent</span>
                    </div>
                  </div>
                  <div className="payment-stat">
                    <Receipt size={18} />
                    <div>
                      <span className="stat-value">{payments.length}</span>
                      <span className="stat-label-text">Transactions</span>
                    </div>
                  </div>
                  <div className="payment-stat">
                    <CheckCircle size={18} />
                    <div>
                      <span className="stat-value">
                        {payments.filter((p) => p.status === 'succeeded').length}
                      </span>
                      <span className="stat-label-text">Successful</span>
                    </div>
                  </div>
                </div>
                <div className="payments-table-wrapper">
                  <table className="payments-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Session</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                              <Calendar size={14} />
                              {formatDateInTz(payment.created_at, profile.timezone || 'UTC')}
                            </div>
                          </td>
                          <td>
                            <strong style={{ fontSize: 13 }}>
                              {payment.session_title || 'Mock Interview Session'}
                            </strong>
                            {payment.scheduled_at && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                                <Clock size={12} />
                                {formatDateInTz(payment.scheduled_at, profile.timezone || 'UTC')}
                              </div>
                            )}
                          </td>
                          <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            ${payment.amount.toFixed(2)}
                          </td>
                          <td>
                            <span
                              className={`payment-status-badge ${payment.status}`}
                            >
                              {payment.status === 'succeeded' ? (
                                <CheckCircle size={12} />
                              ) : (
                                <AlertCircle size={12} />
                              )}
                              {payment.status.charAt(0).toUpperCase() +
                                payment.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'account' && (
        <div className="tab-content">
          <div className="card">
            <div className="card-hdr">
              <Lock size={20} />
              <h3>Change Password</h3>
            </div>
            <div className="profile-form">
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwords.current}
                  onChange={(e) =>
                    setPasswords({ ...passwords, current: e.target.value })
                  }
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={passwords.new_password}
                    onChange={(e) =>
                      setPasswords({
                        ...passwords,
                        new_password: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={passwords.confirm}
                    onChange={(e) =>
                      setPasswords({ ...passwords, confirm: e.target.value })
                    }
                  />
                </div>
              </div>
              <button
                className="btn btn-primary"
                onClick={handlePasswordChange}
                disabled={saving}
              >
                <Lock size={16} />
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .profile-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          border-radius: 10px;
          margin-bottom: 24px;
          font-size: 14px;
        }

        .profile-message.success {
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
          border: 1px solid rgba(34, 197, 94, 0.2);
        }

        .profile-message.error {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .profile-tabs-wrap {
          position: relative;
          margin-bottom: 24px;
        }

        .profile-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid var(--border);
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .profile-tabs::-webkit-scrollbar {
          display: none;
        }

        .profile-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          background: none;
          font-size: 14px;
          font-weight: 500;
          color: var(--text-muted);
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .profile-tab:hover {
          color: var(--primary-navy);
        }

        .profile-tab.active {
          color: var(--primary-navy);
          border-bottom-color: var(--primary-navy);
        }

        .tab-content {
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .card-hdr {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: var(--primary-navy);
        }

        .card-hdr h3 {
          font-size: 18px;
          font-weight: 600;
        }

        .profile-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .field-hint {
          font-size: 13px;
          color: var(--text-muted);
          margin-bottom: 10px;
          line-height: 1.5;
        }

        .toggle-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 0;
          border-bottom: 1px solid var(--border);
          cursor: pointer;
          font-size: 14px;
        }

        .toggle-row:last-child {
          border-bottom: none;
        }

        .toggle-row input[type="checkbox"] {
          width: 20px;
          height: 20px;
          accent-color: var(--primary-navy);
        }

        .consent-info {
          background: var(--accent-light, #f8f9fa);
          padding: 20px;
          border-radius: 10px;
          font-size: 14px;
          line-height: 1.7;
        }

        .consent-info ul {
          margin-top: 12px;
          padding-left: 20px;
        }

        .consent-info li {
          margin-bottom: 6px;
          color: var(--text-dark);
        }

        .profile-actions {
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          margin-top: 24px;
        }

        .empty-payments {
          text-align: center;
          padding: 40px 20px;
          color: var(--text-muted);
        }

        .empty-payments svg {
          margin-bottom: 16px;
          opacity: 0.4;
        }

        .empty-payments h4 {
          color: var(--text-dark);
          margin-bottom: 8px;
        }

        .payments-summary-row {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }

        .payment-stat {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: #f8fafc;
          border: 1px solid var(--border);
          border-radius: 10px;
        }

        .payment-stat svg {
          color: var(--primary-navy);
        }

        .payment-stat .stat-value {
          display: block;
          font-size: 20px;
          font-weight: 700;
          color: var(--text-dark);
        }

        .payment-stat .stat-label-text {
          font-size: 12px;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .payments-table-wrapper {
          overflow-x: auto;
          border: 1px solid var(--border);
          border-radius: 10px;
        }

        .payments-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .payments-table th {
          background: #f8fafc;
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted);
          border-bottom: 1px solid var(--border);
        }

        .payments-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #f1f5f9;
          color: var(--text-dark);
        }

        .payments-table tr:last-child td {
          border-bottom: none;
        }

        .payments-table tr:hover td {
          background: #fafbfc;
        }

        .payment-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }

        .payment-status-badge.succeeded {
          background: #d1fae5;
          color: #065f46;
        }

        .payment-status-badge.pending {
          background: #fef3c7;
          color: #92400e;
        }

        .payment-status-badge.failed {
          background: #fee2e2;
          color: #991b1b;
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          .profile-tabs {
            gap: 0;
            padding-right: 40px;
          }
          .profile-tab {
            padding: 10px 14px;
            font-size: 13px;
          }
          .payments-summary-row {
            flex-direction: column;
          }
          /* fade-right indicator so user knows tabs are scrollable */
          .profile-tabs-wrap::after {
            content: '';
            position: absolute;
            right: 0;
            top: 0;
            width: 48px;
            height: calc(100% - 1px);
            background: linear-gradient(to right, transparent, white 75%);
            pointer-events: none;
            z-index: 1;
          }
        }

        @media (max-width: 480px) {
          .profile-tab { padding: 9px 12px; font-size: 12px; }
          .form-row { gap: 14px; }
        }

        @media (max-width: 360px) {
          .profile-tab { padding: 8px 10px; font-size: 11px; }
        }
      `}</style>
    </div>
  )
}
